<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from 'vue';
  import type { PropType } from 'vue';
  import { gsap } from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  import type { HeroSectionData } from '@/models/hero';

  gsap.registerPlugin(ScrollTrigger);

  defineProps({
    heroSectionData: {
      type: Object as PropType<HeroSectionData>,
      required: true
    }
  });

  const wrapperRef = ref<HTMLElement | null>(null);

  const contentRef = ref<HTMLElement | null>(null);

  const bgRef = ref<HTMLElement | null>(null);

  const heroImagesRef = ref<HTMLElement[] | null>(null);

  const matchMediaRef = ref<ReturnType<typeof gsap.matchMedia>>();

  const initAnimation = () => {
    if (!wrapperRef.value || !contentRef.value || !bgRef.value) return;

    matchMediaRef.value = gsap.matchMedia();

    matchMediaRef.value.add('(min-width: 1024px)', () => {
      gsap.set(contentRef.value, {
        transform: 'translate(0, 0)'
      });
      gsap.set(bgRef.value, {
        transform: 'scale(1) translate(0, 0)'
      });

      if (heroImagesRef.value && heroImagesRef.value.length == 2) {
        gsap.set(heroImagesRef.value?.[0], {
          opacity: 0,
          transform: 'translate(300px, 0)'
        });
        gsap.set(heroImagesRef.value?.[1] as any, {
          opacity: 0,
          transform: 'translate(-300px, 0)'
        });
      }

      gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.value,
          start: 'top-=120px top',
          end: 'top-=120px top',
          scrub: false,
          onEnter: () => {
            gsap
              .timeline()
              .to(contentRef.value, {
                transform: 'translate(0, -128px)',
                duration: 0.6,
                ease: 'power2.out'
              })
              .to(contentRef.value, {
                transform: 'translate(0, -125px)',
                duration: 0.6,
                ease: 'power2.out'
              });
          },
          onEnterBack: () => {
            gsap.timeline().to(contentRef.value, {
              transform: 'translate(0, 0)',
              duration: 0.6,
              ease: 'power2.out'
            });
          }
        }
      });

      gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.value,
          start: 'top-=120px top',
          end: 'top-=120px top',
          scrub: false,
          onEnter: () => {
            gsap
              .timeline()
              .to(bgRef.value, {
                transform: 'scale(0.58) translate(0, 350px)',
                duration: 0.6,
                ease: 'power2.out'
              })
              .to(bgRef.value, {
                transform: 'scale(0.6) translate(0, 350px)',
                duration: 0.6,
                ease: 'power2.out'
              });
          },
          onEnterBack: () => {
            gsap.timeline().to(bgRef.value, {
              transform: 'scale(1) translate(0, 0)',
              duration: 0.6,
              ease: 'power2.out'
            });
          }
        }
      });

      if (heroImagesRef.value && heroImagesRef.value.length == 2) {
        gsap.timeline({
          scrollTrigger: {
            trigger: wrapperRef.value,
            start: 'top-=120px top',
            end: 'top-=120px top',
            scrub: false,
            onEnter: () => {
              gsap
                .timeline()
                .to(heroImagesRef.value?.[0] as any, {
                  opacity: 1,
                  transform: 'translate(-5px, 0)',
                  duration: 0.6,
                  ease: 'power2.out'
                })
                .to(heroImagesRef.value?.[0] as any, {
                  transform: 'translate(0, 0)',
                  duration: 0.6,
                  ease: 'power2.out'
                });

              gsap
                .timeline()
                .to(heroImagesRef.value?.[1] as any, {
                  opacity: 1,
                  transform: 'translate(5px, 0)',
                  duration: 0.6,
                  ease: 'power2.out'
                })
                .to(heroImagesRef.value?.[1] as any, {
                  transform: 'translate(0, 0)',
                  duration: 0.6,
                  ease: 'power2.out'
                });
            },
            onEnterBack: () => {
              gsap.to(heroImagesRef.value?.[0] as any, {
                opacity: 0,
                transform: 'translate(300px, 0)',
                duration: 0.6,
                ease: 'power2.out'
              });

              gsap.to(heroImagesRef.value?.[1] as any, {
                opacity: 0,
                transform: 'translate(-300px, 0)',
                duration: 0.6,
                ease: 'power2.out'
              });
            }
          }
        });
      }
    });
  };

  const onPackagesClick = () => {
    const element = document.querySelector('.use-case-packages-section');

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  onMounted(() => {
    initAnimation();
  });

  onUnmounted(() => {
    matchMediaRef.value?.revert();
    matchMediaRef.value = undefined;
  });
</script>

<template>
  <section>
    <div class="container">
      <div class="use-case-hero-section">
        <div
          ref="wrapperRef"
          class="use-case-hero-section__wrapper">
          <div
            ref="contentRef"
            class="use-case-hero-section__content">
            <div class="use-case-hero-section__tag">
              <div class="use-case-hero-section__icon">
                <nuxt-icon
                  v-if="heroSectionData.icon"
                  :name="`telekom:${heroSectionData.icon}`" />
              </div>
              <div class="use-case-hero-section__name">{{ heroSectionData.title }}</div>
            </div>
            <h1 class="use-case-hero-section__title">{{ heroSectionData.heroTitle }}</h1>
            <p class="use-case-hero-section__subtitle">{{ heroSectionData.heroSubtitle }}</p>
            <a
              class="use-case-hero-section__link"
              @click.prevent.stop="onPackagesClick">
              {{ $t('useCase.packagesBtnText') }}
              <img
                src="@/assets/images/arrow-right.svg"
                alt="icon" />
            </a>
          </div>
          <div class="use-case-hero-section__messages">
            <div
              v-for="(message, index) in heroSectionData.heroMessages"
              :key="index"
              ref="heroImagesRef"
              class="use-case-hero-message">
              <div class="use-case-hero-message__image" />
              <div class="use-case-hero-message__content">
                <div class="use-case-hero-message__title">{{ message.title }}</div>
                <div
                  v-if="message.subtitle"
                  class="use-case-hero-message__subtitle">
                  {{ message.subtitle }}
                </div>
              </div>
            </div>
          </div>
          <div
            ref="bgRef"
            class="use-case-hero-section__bg"
            :style="{ '--bg-image': `url(${heroSectionData.cover?.src})` }"></div>
        </div>
      </div>
    </div>
  </section>
</template>
